import { Box, Checkbox, Divider, FormControl, InputLabel, ListItemText, MenuItem, Select, Stack } from '@mui/material'
import { StatusChip } from '@src/components/StatusChip'
import TagChip from '@src/components/TagChip'
import { EProcessingStatus } from '@src/types/automationDocument'
import { PREDEFINED_TAGS } from '@src/types/constants'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ManualDocsAutomationFiltersType } from './ToolBar'

interface FilterSectionProps {
  defaultValues?: ManualDocsAutomationFiltersType | null
  onFiltersChange: (filters: Partial<ManualDocsAutomationFiltersType>) => void
}

const compactFormControlSx = { width: 200, '& .MuiInputBase-root': { height: 32 } } as const
const compactLabelSx = {
  pl: 1,
  top: '50%',
  transform: 'translateY(-50%)',
  '&.MuiInputLabel-shrink': { top: 0, transform: 'translate(14px, -9px) scale(0.75)' },
} as const

const FilterSection = ({ defaultValues, onFiltersChange }: FilterSectionProps) => {
  const { t } = useTranslation()

  const [status, setStatus] = useState<EProcessingStatus[]>(defaultValues?.status ?? [])
  const [tags, setTags] = useState<string[]>(defaultValues?.tags ?? [])

  useEffect(() => { setStatus(defaultValues?.status ?? []) }, [defaultValues?.status])
  useEffect(() => { setTags(defaultValues?.tags ?? []) }, [defaultValues?.tags])

  const statusOptions = useMemo(
    () =>
      Object.values(EProcessingStatus).map((value) => ({
        value,
        label: t(`enum.processingStatus.${value}` as const),
      })),
    [t],
  )

  const handleStatusChange = (newStatus: EProcessingStatus[]) => {
    setStatus(newStatus)
    onFiltersChange({ status: newStatus.length > 0 ? newStatus : null })
  }

  const handleTagsChange = (newTags: string[]) => {
    setTags(newTags)
    onFiltersChange({ tags: newTags.length > 0 ? newTags : null })
  }

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <FormControl size="small" sx={compactFormControlSx}>
        <InputLabel id="status-select-label" sx={compactLabelSx}>{t('docsAutomation.processingStatus')}</InputLabel>
        <Select
          labelId="status-select-label"
          multiple
          value={status}
          label={t('docsAutomation.processingStatus')}
          onChange={(e) => {
            const v = typeof e.target.value === 'string' ? e.target.value.split(',') : (e.target.value as string[])
            if (v.includes('__all__')) {
              const allValues = statusOptions.map((o) => o.value)
              handleStatusChange(status.length === statusOptions.length ? [] : allValues)
            } else {
              handleStatusChange(v as EProcessingStatus[])
            }
          }}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5, overflow: 'hidden', alignItems: 'center' }}>
              <StatusChip status={selected[0]} shimmer={false} />
              {selected.length > 1 && <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>(+{selected.length - 1})</span>}
            </Box>
          )}
        >
          <MenuItem value="__all__">
            <Checkbox checked={status.length === statusOptions.length} />
            <ListItemText primary={t('common.selectAll')} />
          </MenuItem>
          <Divider />
          {statusOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Checkbox checked={status.includes(option.value)} />
              <StatusChip status={option.value} shimmer={false} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={compactFormControlSx}>
        <InputLabel id="tags-select-label" sx={compactLabelSx}>{t('docsAutomation.tags')}</InputLabel>
        <Select
          labelId="tags-select-label"
          multiple
          value={tags}
          label={t('docsAutomation.tags')}
          onChange={(e) => {
            const v = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
            if (v.includes('__all__')) {
              handleTagsChange(tags.length === PREDEFINED_TAGS.length ? [] : [...PREDEFINED_TAGS])
            } else {
              handleTagsChange(v)
            }
          }}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5, overflow: 'hidden', alignItems: 'center' }}>
              {selected.slice(0, 2).map((val) => (
                <TagChip key={val} tag={val} size="small" />
              ))}
              {selected.length > 2 && <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>+{selected.length - 2}</span>}
            </Box>
          )}
        >
          <MenuItem value="__all__">
            <Checkbox checked={tags.length === PREDEFINED_TAGS.length} />
            <ListItemText primary={t('common.selectAll')} />
          </MenuItem>
          <Divider />
          {PREDEFINED_TAGS.map((tag) => (
            <MenuItem key={tag} value={tag}>
              <Checkbox checked={tags.includes(tag)} />
              <TagChip tag={tag} size="small" sx={{ mr: 1 }} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  )
}

export default FilterSection
